import { DocumentsRequestCategory } from '../../documents/documents.model';

export enum AlertResolutionAction {
  ADJUSTMENT = 'Account adjustment',
  INTERACTION = 'Engaged directly with player',
  COMMUNICATION = 'Sent message to player',
  OTHER = 'Other',
}

export enum OutcomeCategory {
  INITIAL_CONTACT = 'INITIAL_CONTACT',
  RAISE_AWARENESS = 'RAISE_AWARENESS',
  EXPLORE_BEHAVIOURS = 'EXPLORE_BEHAVIOURS',
  RISKS_IDENTIFIED = 'RISKS_IDENTIFIED',
  CONCERNS_IDENTIFIED = 'CONCERNS_IDENTIFIED',
  INCREASED_LEVEL_OF_CONCERNS = 'INCREASED_LEVEL_OF_CONCERNS',
  REPEATED_OR_ONGOING_CONCERNS = 'REPEATED_OR_ONGOING_CONCERNS',
  MAJOR_CONCERNS = 'MAJOR_CONCERNS',
  NO_ACTION = 'NO_ACTION',
}

export enum AlertResolutionActionV2 {
  EMAIL = 'EMAIL',
  IN_APP_MESSAGE = 'IN_APP_MESSAGE',
  SMS = 'SMS',
  LIVE_CHAT = 'LIVE_CHAT',
  CALL_NO_ANSWER = 'CALL_NO_ANSWER',
  CALL_SUCCESSFUL = 'CALL_SUCCESSFUL',
  PLAY_SENSE_APPLIED = 'PLAY_SENSE_APPLIED',
  ACCOUNT_RESTRICTED = 'ACCOUNT_RESTRICTED',
  COOL_DOWN = 'COOL_DOWN',
}

export interface DetailedAlertDTO {
  id: number;
  playerId: number;
  playerStatus: string;
  playerName: string;
  resolution: AlertResolutionDTO;
  triggers: ActiveAlertTriggersDTO[];
}

export interface AlertResolutionDTO {
  decisionedByName: string;
  decisionedByEmail: string;
  outcome: string;
  actionsTaken: string[];
  description: string;
  dateCreated: Date;
  actionsTakenV2: ResolutionActionsV2;
}

export interface DetailedActiveAlertDTO {
  id: number;
  playerId: number;
  hvp: boolean;
  playerStatus: string;
  playerName: string;
  lock?: AlertLockDTO;
  triggers: ActiveAlertTriggersDTO[];
}

export interface DetailedPlayerAlertParamsDTO {
  triggers: DetailedTriggerParams;
  resolveOnlyActiveAlerts: boolean;
}

export interface AlertResolutionReportDTO {
  alertId: number;
  generationStarted: Date;
  generationFinished: Date;
  account: AlertReportAccountDTO;
  fraud: AlertReportFraudDTO;
  saferGambling: AlertReportSaferGamblingDTO;
  patterns: AlertReportPatternsDTO;
  financials: AlertReportFinancialsDTO;
  internals: AlertReportInternalsDTO;
}

export interface AlertReportAccountDTO {
  age: number;
  registered: string;
  email: string;
  currencyCode: string;
  employmentStatus: string;
  occupationCategory: string;
}

export interface AlertReportFraudDTO {
  seonScore: number;
}

export interface AlertReportSaferGamblingDTO {
  cooldownsInLast90Days?: number;
  autoRgInteractions: number;
  playerSelfExclusions: number;
  playerTimeouts: number;
  currentOrLastSessionMinutes: number;
  timePlayedLast24HMinutes: number;
  insufficientFundsCount: number;
  isPreviousGamstop: boolean;
  tuScore?: number;
  depositsIn24HrsCount?: number;
  withdrawalsIn24HrsCount?: number;
}

export interface AlertReportPatternsDTO {
  depositAverages: DepositAveragesDTO;
  stakesAverageLifetimeBeforeLast24h: string;
  stakeAverageLast24h: string;
}

export interface AlertReportFinancialsDTO {
  depositsToday: string;
  withdrawalsToday: string;
  netLossToday: string;
  depositsThisMonth: string;
  withdrawalsThisMonth: string;
  netLossThisMonth: string;
  depositsLifetime: string;
  withdrawalsLifetime: string;
  netLossLifetime: string;
  totalStakes: string;
  cashStakes: string;
  bonusStakes: string;
  totalBalance: string;
  cashBalance: string;
  bonusBalance: string;
}

export interface AlertReportInternalsDTO {
  manualCommentsCount: number;
  lastCommentDate: Date;
  lastCommentAuthor: string;
  lastComment: string;
  activeDocumentsRequests: ActiveDocumentsRequestDTO[];
}

export interface ActiveDocumentsRequestDTO {
  id: number;
  categories: DocumentsRequestCategory[];
}

export interface DepositAveragesDTO {
  lifetimeAverage: string;
  last24hAverage: string;
}

export interface DetailedTriggerParams {
  depositsSumIn24hThresholds: { statusChange: string; warnings: string[] };
  bigWinThresholdValue: string;
  declinedDepositsIn24hThreshold: number;
  frequentDepositingIn24hThreshold: { count: number; minValue: string };
  lifetimeDepositsThresholds: Map<string, string>;
  manyCardsThreshold: number;
  multipleTimeoutsThreshold: number;
  singleLargeDepositThreshold: string;
  lifetimeNetLossThresholds: Map<string, string>;
  monthlyAffordability: Map<string, Array<{ ageMin: number; ageMax: number; netLossThreshold: string }>>;
}
export interface AlertLockDTO {
  agentEmail: string;
  agentFullName: string;
  OffsetDateTime: Date;
  dateCreated: Date;
}

export interface ActiveAlertTriggersDTO {
  detail: string;
  name: string;
  tags: Array<string>;
  triggerDate: Date;
  type: string;
  snoozeId: number;
  justification: string[];
}

export interface urlParameters {
  pageIndex: number;
  pageSize: number;
  id: number;
  sortBy: SortBy;
  order: SortOrder;
  tabName: string;
  playerId?: number;
  lockedBy?: string;
}
export interface PageDTO<T> {
  number: number;
  size: number;
  allElements: number;
  content: T[];
}

export interface PageReq extends AlertsSearchCriteria, AlertsSortCriteria {
  number?: number;
  size?: number;
  id?: number;
  tabName?: string;
  lockedBy?: string;
}

interface ResolutionActionsV2 {
  outcomeCategory: OutcomeCategory;
  resolutionActions: AlertResolutionActionV2[];
}

export interface AddResolutionDTO {
  playerId: number;
  actionsTaken: AlertResolutionAction[];
  description: string;
}

export interface AddResolveDTO {
  actionsTaken?: AlertResolutionAction[];
  description: string;
  resolutionActionsV2?: ResolutionActionsV2;
}

export interface ResolutionDraft {
  updateTime: Date;
  modalState: ModalState;
  resolutionData: AddResolveDTO;
}

export enum ModalState {
  CLOSED = 'CLOSED',
  OPENED = 'OPENED',
  MINIMIZED = 'MINIMIZED',
}

export interface TableHeader {
  title: string;
  isSortable: boolean;
  isCustomHeader?: boolean;
  isHidden?: boolean;
  customData?: any;
}
export interface AlertsSearchCriteria {
  playerId?: number;
}

export interface AlertsSortCriteria {
  sortBy?: SortBy;
  order?: SortOrder;
  tags?: TriggerType;
}

export enum TriggerType {
  SAFER_GAMBLING = 'SG',
  ANTI_MONEY_LAUNDERING = 'AM',
  EXPERIENCE = 'EX',
  FRAUD = 'FR',
}

export enum SortBy {
  ID = 'ID',
  WHEN = 'RESOLUTION_DATE_CREATED',
}

export enum SortOrder {
  DESC = 'DESC',
  ASC = 'ASC',
}

export enum TabName {
  ACTIVE = 'Active',
  ARCHIVE = 'Archive',
}

export interface PlayerCommentaryDTO {
  id: number;
  playerId: number;
  header: string;
  commentary: string;
  commentaryTime: Date;
  adminEmail: string;
  tags: string[];
}

export interface AlertItem {
  triggers: string;
  category: string;
  actions: string;
  description: string;
  date: Date;
  resolvedBy: string;
  expanded?: boolean;
}
